import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import layout from "./Payments.module.css";
import styles from "./AdminReviews.module.css";

const stars = (n = 0) => "★".repeat(Math.max(0, n)) + "☆".repeat(Math.max(0, 5 - n));

const emptyReviewPlaceholders = [
  {
    id: "ph-1",
    reviewer_name: "Guest Reviewer",
    room_type: { title: "Room type" },
    rating: 5,
    comment: "Great stay and very comfortable experience.",
    created_at: new Date().toISOString(),
  },
  {
    id: "ph-2",
    reviewer_name: "New Customer",
    room_type: { title: "Room type" },
    rating: 4,
    comment: "Clean room, friendly staff, and smooth booking.",
    created_at: new Date().toISOString(),
  },
  {
    id: "ph-3",
    reviewer_name: "Happy Guest",
    room_type: { title: "Room type" },
    rating: 5,
    comment: "Lovely ambience. Will definitely come back.",
    created_at: new Date().toISOString(),
  },
];

export default function AdminReviews() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const [selectedRoomType, setSelectedRoomType] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("all");

  const fetchRoomTypes = async () => {
    try {
      const response = await api.get("/admin/room-types");
      setRoomTypes(response.data?.roomTypes || []);
    } catch {
      setRoomTypes([]);
    }
  };

  const fetchReviews = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (selectedRoomType) params.room_type_id = selectedRoomType;
      if (approvalFilter !== "all") params.is_approved = approvalFilter;

      const response = await api.get("/reviews", { params });
      const reviewRows = response.data?.data || [];
      setReviews(reviewRows);
    } catch (err) {
      setReviews([]);
      setError(err?.response?.data?.message || "Failed to load reviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [selectedRoomType, approvalFilter]);

  const onToggleApproval = async (reviewId) => {
    setBusyId(reviewId);
    try {
      await api.patch(`/reviews/${reviewId}/approval`);
      await fetchReviews();
    } catch {
      setError("Could not update review approval.");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (reviewId) => {
    if (!window.confirm("Delete this review?")) return;
    setBusyId(reviewId);
    try {
      await api.delete(`/reviews/${reviewId}`);
      await fetchReviews();
    } catch {
      setError("Could not delete review.");
    } finally {
      setBusyId(null);
    }
  };

  const visibleReviews = useMemo(() => {
    if (reviews.length > 0) return reviews;
    return emptyReviewPlaceholders;
  }, [reviews]);

  const approvedCount = reviews.filter((review) => review.is_approved).length;
  const pendingCount = reviews.filter((review) => !review.is_approved).length;

  return (
    <div className={layout.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {sidebarOpen && <div className={layout.overlay} onClick={() => setSidebarOpen(false)} />}

      <div className={layout.content}>
        <Header pageTitle="Reviews" onMenuClick={() => setSidebarOpen(true)} />

        <div className={styles.page}>
          <section className={styles.topSection}>
            <div className={styles.introCard}>
              <div>
                <p className={styles.eyebrow}>Guest Feedback</p>
                <h2 className={styles.sectionTitle}>Manage published and pending reviews</h2>
                <p className={styles.sectionText}>
                  Filter by room type or approval status, then moderate guest feedback from one place.
                </p>
              </div>

              <div className={styles.statsRow}>
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Showing</span>
                  <strong className={styles.statValue}>{visibleReviews.length}</strong>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Approved</span>
                  <strong className={styles.statValue}>{approvedCount}</strong>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Pending</span>
                  <strong className={styles.statValue}>{pendingCount}</strong>
                </div>
              </div>
            </div>

            <div className={styles.toolbarCard}>
              <div className={styles.controls}>
                <select
                  className={styles.select}
                  value={selectedRoomType}
                  onChange={(e) => setSelectedRoomType(e.target.value)}
                >
                  <option value="">All room types</option>
                  {roomTypes.map((roomType) => (
                    <option key={roomType.id} value={roomType.id}>
                      {roomType.title}
                    </option>
                  ))}
                </select>

                <select
                  className={styles.select}
                  value={approvalFilter}
                  onChange={(e) => setApprovalFilter(e.target.value)}
                >
                  <option value="all">All statuses</option>
                  <option value="true">Approved</option>
                  <option value="false">Pending</option>
                </select>
              </div>
            </div>
          </section>

          <div className={styles.contentSection}>
            {error && <p className={styles.error}>{error}</p>}
            {loading && <p className={styles.helper}>Loading reviews...</p>}

            {!loading && reviews.length === 0 && (
              <p className={styles.helper}>
                No reviews yet. Showing placeholder reviews so your admin layout stays consistent.
              </p>
            )}

            <div className={styles.grid}>
              {visibleReviews.map((review) => {
                const isPlaceholder = review.id.toString().startsWith("ph-");
                return (
                  <article
                    key={review.id}
                    className={`${styles.card} ${isPlaceholder ? styles.placeholderCard : ""}`}
                  >
                    <div className={styles.cardHeader}>
                      <div>
                        <h3 className={styles.reviewer}>{review.reviewer_name || "Guest"}</h3>
                        <p className={styles.roomType}>{review.room_type?.title || "Room"}</p>
                      </div>
                      <span className={styles.rating}>{stars(Number(review.rating || 0))}</span>
                    </div>

                    <p className={styles.comment}>
                      {review.comment || "No comment provided."}
                    </p>

                    <div className={styles.metaRow}>
                      <span className={styles.date}>
                        {new Date(review.created_at).toLocaleDateString("en-GB")}
                      </span>
                      {!isPlaceholder && (
                        <span className={review.is_approved ? styles.approved : styles.pending}>
                          {review.is_approved ? "Approved" : "Pending"}
                        </span>
                      )}
                    </div>

                    {!isPlaceholder && (
                      <div className={styles.actions}>
                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={() => onToggleApproval(review.id)}
                          disabled={busyId === review.id}
                        >
                          {review.is_approved ? "Mark Pending" : "Approve"}
                        </button>
                        <button
                          type="button"
                          className={styles.dangerBtn}
                          onClick={() => onDelete(review.id)}
                          disabled={busyId === review.id}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
